import { bindActionCreators } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import shoppingCartActions from "../../store/actions/shoppingCart/shoppingCart.actions";
import AlertService from "../../services/alerts/alert.service";
import ShoppingCartQuantityInputComponent from "../../components/shoppingCart/ShoppingCartQuantityInputComponent";
import TextAreaInputElementComponent from "../../components/general/elements/inputElements/TextAreaInputElementComponent";

import "../../styles/pages/shoppingCart/shoppingCartPageComponent.scss";
import shoppingCartService from "../../services/shoppingCart/shoppingCart.service";
import useAccount from "../../hooks/useAccount";
import orderActions from "../../store/actions/orders/order.actions";
import enctyptionHelper from "../../helpers/encryption.helper";
import textInputModel from "../../models/general/elements/inputElements/textInput.model";
import LoaderComponent from "../../components/general/LoaderComponent";
import productActions from "../../store/actions/items/item.actions";
import validationHelper from "../../helpers/validation.helper";
import numbersHelper from "../../helpers/numbers.helper";
import agentOrderActions from "../../store/actions/agentOrder/agentOrder.actions";

export const ShoppingCartPageComponent = ({ shoppingCartActions, orderActions }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cartItemList = useSelector((state) => state.shoppingCart.shoppingCartItems);
  // M3: when an agent/admin has picked a customer, the order is placed on that customer's behalf.
  const selectedCustomer = useSelector((state) => state.agentOrderReducer.selectedCustomer);
  const { isLoggedIn, userInfo } = useAccount();
  const [comment, setComment] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isHistoryItemsDialogOpened, setIsHistoryItemsDialogOpened] = useState(false);

  useEffect(() => {
    if (!isHistoryItemsDialogOpened && cartItemList.length > 0) {
      navigate(`?d=shoppingCart&action=recommendedItems`);
      setIsHistoryItemsDialogOpened(true);
    }
  }, [cartItemList, userInfo, productActions]);

  const addItemCartQuantity = (cartItem) => {
    const updatedCartItem = { ...cartItem, quantity: cartItem.quantity + cartItem.itemPackSize }; // Create a new object
    shoppingCartActions.updateCartItem(updatedCartItem);
  };

  const reduceItemCartQuantity = async (cartItem) => {
    const newQuantity = cartItem.quantity - cartItem.itemPackSize;

    if (newQuantity >= cartItem.itemPackSize) {
      const updatedCartItem = { ...cartItem, quantity: newQuantity }; // Create a new object
      shoppingCartActions.updateCartItem(updatedCartItem);
      return;
    }

    const userChoice = await AlertService.confirm("האם אתה בטוח שברצונך למחוק פריט זה מסל הפריטים שלך?", "שים לב! תוכל להוסיף את הפריט לסל שוב מעמוד הפריט", "כן", "לא");
    if (userChoice === true) shoppingCartActions.removeFromCart(cartItem.id);
  };

  const handleCommentChange = (event) => {
    const { value } = event.target;
    setComment(value);
  };

  const clearShoppingCartClicked = async (event) => {
    event.preventDefault();
    const userChoice = await AlertService.confirm("האם אתה בטוח שברצונך לרוקן את סל הקניות?", "שים לב, פעולה זו בלתי הפיכה", "כן", "לא");
    if (userChoice) {
      shoppingCartActions.clearCart();
    }
  };

  const handleCreateOrder = async () => {
    try {
      if (!enableOrderButton()) {
        AlertService.warning("לא ניתן לבצע הזמנה", "על מנת לבצע הזמנה, יש להתחבר למשתמש אותו קיבלת. לקבלת משתמש ופרטים נוספים, יש ליצור איתנו קשר.");
        return;
      }

      // M3: agent/admin orders go to the selected customer; a business user orders for itself.
      // The server re-derives the placer from the token and re-checks the customer is allowed.
      // 0017 (D4): never fall back to an undefined id. An admin/agent has no businessCustomer, so if
      // their selection is missing we must BLOCK the order with a clear message rather than place it
      // against `undefined` (which would throw or, worse, silently target the wrong customer).
      const targetCustomerId = selectedCustomer?.customerId ?? userInfo?.businessCustomer?.customerId;
      if (!targetCustomerId) {
        AlertService.warning("לא ניתן לבצע הזמנה", "לא נבחר לקוח להזמנה. יש לבחור לקוח לפני ביצוע ההזמנה.");
        return;
      }

      setIsLoading(true);

      // Construct CreateOrderRequest
      const createOrderRequest = {
        userId: userInfo.id,
        comment,
        customerId: targetCustomerId,
        totalPriceBeforeVat: shoppingCartService.calculateTotalPrice(cartItemList),
        totalPrice: shoppingCartService.calculateTotalPriceWithVat(cartItemList, 18),
        vatSum: shoppingCartService.calculateVatPrice(shoppingCartService.calculateTotalPrice(cartItemList), 18), // Calculate VAT sum if needed
        vatPercent: 18, // Add VAT percent if applicable
        cartItems: cartItemList.map((item) => ({
          id: item.id,
          rivhit_ItemId: item.itemId,
          itemDescription: item.itemDescription,
          salePriceInNIS: item.salePriceInNIS,
          quantity: item.quantity,
        })),
      };
      // Dispatch action to create order
      const response = await orderActions.addOrder(createOrderRequest);
      setIsLoading(false);

      // Encrypt the documentLink
      const encryptedDocumentLink = enctyptionHelper.encrypt(response.payload.documentLink);
      // Navigate with URL parameters
      navigate(`/thank-you?orderNumber=${response.payload.orderNumber}&documentLink=${encodeURIComponent(encryptedDocumentLink)}`);

      shoppingCartActions.clearCart();
      // M3: reset the on-behalf selection so the next order starts clean.
      if (selectedCustomer) dispatch(agentOrderActions.clearSelectedCustomer());
    } catch (error) {
      console.error("Error creating order:", error);
      AlertService.error("שגיאה ביצירת ההזמנה", error.message || "נסה שוב מאוחר יותר.");
    }
  };

  // Get Ellements Functions
  const getCartItemsListTitlesElement = () => {
    return (
      <div className="cartItemsListTitlesContainer">
        <div className="productTitle">מוצר</div>
        <div className="quantityTitle">כמות</div>
        <div className="totalAmountTitle">סה"כ</div>
      </div>
    );
  };

  const getCartItemsListElement = () => {
    return (
      <div className="cartItemsListContainer">
        {cartItemList.map((cartItem, idx) => (
          <div key={idx} className="cartItemCardContainer">
            {getCartItemContentElement(cartItem)}
            {getShoppingCartQuantityElement(cartItem)}
            {getPriceSummaryElement(cartItem)}
          </div>
        ))}
      </div>
    );
  };

  const getCartItemContentElement = (cartItem) => {
    return (
      <div className="itemContentContainer">
        <div className="itemImageContainer">
          <img src={cartItem.pictureUrl} alt={cartItem.itemDescription} />
        </div>
        <div className="itemDetailsContainer">
          <span>{cartItem.itemDescription}</span>
          <span>{cartItem.salePriceInNIS} ₪</span>
        </div>
      </div>
    );
  };

  const getShoppingCartQuantityElement = (cartItem) => {
    const shoppingCartQuantityProps = {
      cartItem,
      handleAdd: addItemCartQuantity,
      handleReduce: reduceItemCartQuantity,
    };

    return (
      <div className="itemCartQuantityContainer">
        <ShoppingCartQuantityInputComponent props={shoppingCartQuantityProps} />
      </div>
    );
  };

  const getPriceSummaryElement = (cartItem) => {
    const totalPrice = numbersHelper.formatToTwoDecimalPlaces(cartItem.salePriceInNIS * cartItem.quantity);
    return <span className="alignTextEnd cartItemCardPriceSummary">{totalPrice} ₪</span>;
  };

  const getOrderCommentsElement = () => {
    const inputProps = {
      ...textInputModel,
      label: "הערות להזמנה",
      name: "comment",
      value: comment,
      clearDesgin: true,
      customStyleClass: "customTextArea",
      onChange: handleCommentChange,
    };
    return <TextAreaInputElementComponent props={inputProps} />;
  };

  const getCartSummaryElement = (cartItem) => {
    return (
      <div className="summaryAndCheckOutContainer">
        <div className="summaryDetailsComtainer">
          <span>סה"כ ללא מע"מ</span>
          <span>{shoppingCartService.calculateTotalPrice(cartItemList)} ₪</span>
        </div>
        <div className="summaryDetailsComtainer">
          <span>מע"מ 18%</span>
          <span>{shoppingCartService.calculateVatPrice(shoppingCartService.calculateTotalPrice(cartItemList), 18)} ₪</span>
        </div>
        <div className="summaryDetailsComtainer">
          <span>סה"כ לתשלום</span>
          <span>{shoppingCartService.calculateTotalPriceWithVat(cartItemList, 18)} ₪</span>
        </div>

        <button className={`primaryButton ${enableOrderButton() ? "" : "disableButton"}`} onClick={handleCreateOrder}>
          בצע הזמנה
        </button>
      </div>
    );
  };

  const enableOrderButton = () => {
    // A business user orders for itself; an agent/admin orders for a selected customer.
    if (isLoggedIn && (userInfo?.businessCustomer || selectedCustomer)) return true;
    return false;
  };

  const getNoItemsInCartElement = () => (
    <div className="noItemsInCartContainer">
      <h2>סל הקניות שלך ריק</h2>
      <button onClick={() => navigate("/")}>חזור לעמוד הראשי</button>
    </div>
  );

  // *******************************
  if (isLoading) return <LoaderComponent />;

  if (cartItemList.length === 0) return getNoItemsInCartElement();

  return (
    <div className="shoppingCartPageContainer container">
      <div className="cartHeaderContainer">
        <h1>סל הקניות שלי</h1>
        <button className="primaryButton" onClick={clearShoppingCartClicked}>
          איפוס סל קניות
        </button>
      </div>

      {selectedCustomer && (
        <div className="onBehalfBanner">
          הזמנה עבור לקוח: <b>{selectedCustomer.firstName} {selectedCustomer.lastName}</b> (קוד {selectedCustomer.customerId})
        </div>
      )}

      {getCartItemsListTitlesElement()}
      {getCartItemsListElement()}

      <div className="summaryContainer">
        {getOrderCommentsElement()}
        {getCartSummaryElement()}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({});

const mapDispatchToProps = (dispatch) => {
  return {
    shoppingCartActions: bindActionCreators(shoppingCartActions, dispatch),
    orderActions: bindActionCreators(orderActions, dispatch),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ShoppingCartPageComponent);
